import { of, switchMap } from "rxjs";
import { take } from "rxjs/operators";

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit
} from '@angular/core';
import { CommonModule, DatePipe, Location, NgIf } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import { MatButton } from '@angular/material/button';
import { MatIcon } from "@angular/material/icon";
import { MatCardModule } from '@angular/material/card';

import { OrganisationService } from "@fry/lib/organisations";
import { CreateMfaDialogComponent } from "@fry/components/accounts/mfa/create-mfa-dialog.component";
import {
  MFA,
  MFAMethod,
  MFAMethodsEnum,
  READABLE_MFA_METHODS
} from "@fry/lib/auth/account/mfa/mfa";
import { MFAService } from "@fry/lib/auth/account/mfa/mfa.service";
import { DialogComponent } from "@fry/components/common/dialog";
import { isDialogError } from "@fry/components/common/dialog/dialog.error";
import { MatGridListModule } from "@angular/material/grid-list";
import { OidcService } from "@fry/lib/core";


@Component({
    selector: 'eas-mfas',
    templateUrl: './list-mfas.component.html',
    styleUrls: ['./list-mfas.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        NgIf,
        MatButton,
        MatCardModule,
        MatIcon,
        MatGridListModule,
        DatePipe
    ]
})
export class ListMfasComponent implements OnInit {
  public remoteUser: string | null = null;
  public isForRemoteUser: boolean = false;
  public credentialUsername: string = '';

  mfas: MFA[] = [];
  readableName = READABLE_MFA_METHODS
  availableMethods: MFAMethod[] = [];

  private location: Location;

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private organisationService: OrganisationService,
    private MFAService: MFAService,
    private cdr: ChangeDetectorRef,
    private oidc: OidcService,
  ) {
    this.location = inject(Location);
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const userParam = params.get('user') || '';
      if (userParam !== 'profile') {
        this.remoteUser = userParam;
        this.isForRemoteUser = true;
      }
      this.credentialUsername = params.get('credentialUsername') || '';
      this.loadUserRegisteredMFAsAndAvailableMethods();
    });
  }

  private async loadUserRegisteredMFAsAndAvailableMethods() {
    this.MFAService.getRegisteredMFAs(this.credentialUsername, this.remoteUser).subscribe((mfas) => {
      this.mfas = mfas;
      this.organisationService.getCurrent()
        .subscribe((organisation) => {
          const registeredMethods = this.mfas.map(mfa => mfa.type);
          this.availableMethods = organisation.enabledMFAs.filter(method => !registeredMethods.includes(method.id));
          this.cdr.markForCheck();
        });
    });
  }

  canAdd(): boolean {
    return this.availableMethods.length > 0 && !this.isForRemoteUser;
  }

  canReplaceMFA(mfa: MFA): boolean {
    return mfa.type === MFAMethodsEnum.TOTP && !this.isForRemoteUser;
  }

  canDelete(): boolean {
    if (this.isForRemoteUser) {
      return true;
    }
    return this.mfas.length !== 1
  }

  async createMFA(preSelectMethod?: MFAMethodsEnum) {
    await this.oidc.ensureStepup();

    const dialogRef = this.dialog.open(CreateMfaDialogComponent, {
      minWidth: '30vw',
      disableClose: true,
      data: {
        credentialUsername: this.credentialUsername,
        availableMethods: this.availableMethods,
        preSelectMethod: preSelectMethod
      },
    });

    dialogRef.componentInstance.completed.subscribe((result: any) => {
      dialogRef.close();
      if (!result.method) {
        return;
      }

      this.snackBar.open(`You've successfully setup two factor authentication!`, 'Close', { duration: 3000 });
      this.loadUserRegisteredMFAsAndAvailableMethods();
    });
  }

  async deleteMFA(mfaId: string) {
    await this.oidc.ensureStepup();
    const dialog = this.dialog.open(DialogComponent, {
      data: {
        title: $localize`Are you sure?`,
        content: $localize`Are you sure you want to delete this two factor authentication method? This action cannot be undone.`,
        okButton: $localize`Yes, delete it!`,
        cancelButton: $localize`No, keep it`
      }
    });
    return dialog.afterClosed().pipe(
      switchMap(result => {
        if (!result || isDialogError(result)) {
          return of(null);
        }

        return new Promise((resolve, reject) => {
          this.MFAService.deleteMFA(this.credentialUsername, mfaId, this.remoteUser)
            .pipe(take(1))
            .subscribe({
              next: (response) => {
                this.snackBar.open('2-factor authentication deleted successfully', 'Close', { duration: 3000 });
                this.loadUserRegisteredMFAsAndAvailableMethods();
                resolve(response);
              },
              error: (err) => {
                this.snackBar.open(`Failed to delete 2-factor authentication: ${err}`, 'Close', { duration: 3000 });
                reject(err);
              }
            });
        });
      })
    ).subscribe();
  }

  goBack() {
    this.location.back();
  }

}
